import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import LocationStamp from "@/components/LocationStamp";
import { currentWeekStart, addDays, isoToMelbourneLocal, melbourneLocalToISO } from "@/lib/time";
import { ChevronLeft, ChevronRight, Pencil, Plus, Lock, Unlock, CheckCircle2, History, Trash2 } from "lucide-react";

export default function AdminStaffDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [week, setWeek] = useState(params.get("week") || currentWeekStart());
  const [data, setData] = useState(null);
  const [editShift, setEditShift] = useState(null);
  const [editForm, setEditForm] = useState({ clock_in: "", clock_out: "", breaks: [], note: "", reason: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ clock_in: "", clock_out: "", note: "", reason: "" });

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/staff/${id}/detail`, { params: { week_start: week } });
    setData(data);
  }, [id, week]);
  useEffect(() => { load(); }, [load]);

  const openEdit = (s) => {
    setEditShift(s);
    setEditForm({
      clock_in: isoToMelbourneLocal(s.clock_in_iso),
      clock_out: isoToMelbourneLocal(s.clock_out_iso),
      breaks: (s.breaks || []).map((b) => ({
        start: isoToMelbourneLocal(b.start_iso),
        end: isoToMelbourneLocal(b.end_iso),
      })),
      note: s.note || "",
      reason: "",
    });
  };

  const saveEdit = async () => {
    try {
      const breaks = (editForm.breaks || [])
        .filter((b) => b.start)
        .map((b) => ({
          start: melbourneLocalToISO(b.start),
          end: b.end ? melbourneLocalToISO(b.end) : null,
        }));
      await api.patch(`/admin/shifts/${editShift.id}`, {
        clock_in_iso: editForm.clock_in ? melbourneLocalToISO(editForm.clock_in) : null,
        clock_out_iso: editForm.clock_out ? melbourneLocalToISO(editForm.clock_out) : null,
        breaks,
        note: editForm.note,
        reason: editForm.reason,
      });
      toast.success("Entry updated.");
      setEditShift(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const addEntry = async () => {
    try {
      await api.post("/admin/shifts", {
        user_id: id,
        clock_in_iso: melbourneLocalToISO(addForm.clock_in),
        clock_out_iso: addForm.clock_out ? melbourneLocalToISO(addForm.clock_out) : null,
        note: addForm.note,
        reason: addForm.reason,
      });
      toast.success("Entry added.");
      setAddOpen(false);
      setAddForm({ clock_in: "", clock_out: "", note: "", reason: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const approve = async () => {
    try {
      await api.post("/admin/approve", { user_id: id, week_start: week });
      toast.success("Timesheet approved and locked.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const reopen = async () => {
    try {
      await api.post("/admin/reopen", { user_id: id, week_start: week });
      toast.success("Timesheet reopened.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!data) return null;
  const locked = data.approved;

  return (
    <div className="space-y-6" data-testid="admin-staff-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">{data.staff.name}</h1>
          <p className="text-muted-foreground">@{data.staff.username} • Weekly total <span className="font-semibold text-foreground">{data.total_display}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setWeek(addDays(week, -7))} data-testid="detail-week-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-medium px-2">{data.week_start_display} – {data.week_end_display}</span>
          <Button variant="outline" size="icon" onClick={() => setWeek(addDays(week, 7))} data-testid="detail-week-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {!locked ? (
          <>
            <Button onClick={() => setAddOpen(true)} variant="outline" data-testid="add-entry-btn"><Plus className="h-4 w-4 mr-2" /> Add Missing Entry</Button>
            <Button onClick={approve} data-testid="approve-btn"><CheckCircle2 className="h-4 w-4 mr-2" /> Approve Timesheet</Button>
          </>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-[hsl(150,45%,28%)] bg-[hsl(150,40%,92%)] px-3 py-2 rounded-lg" data-testid="approved-banner">
              <Lock className="h-4 w-4" /> Approved by {data.approval?.approved_by} on {data.approval?.approved_at_display}
            </div>
            <Button onClick={reopen} variant="outline" data-testid="reopen-btn"><Unlock className="h-4 w-4 mr-2" /> Reopen</Button>
          </div>
        )}
      </div>

      <Card className="rounded-xl border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Clock In</TableHead>
              <TableHead>Clock Out</TableHead>
              <TableHead>Break</TableHead>
              <TableHead>Hours</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Edit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="detail-shift-rows">
            {data.shifts.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No shifts this week.</TableCell></TableRow>}
            {data.shifts.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">
                  {s.date_display}
                  {s.note && <div className="text-xs text-muted-foreground font-normal mt-0.5 italic" data-testid={`shift-note-${s.id}`}>Note: {s.note}</div>}
                </TableCell>
                <TableCell>{s.clock_in_display || "—"}</TableCell>
                <TableCell className={s.flagged ? "text-[hsl(8,54%,42%)] font-medium" : ""}>{s.clock_out_display || "missing"}</TableCell>
                <TableCell>{s.total_break_display}</TableCell>
                <TableCell className="font-semibold">{s.total_worked_display}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {s.clock_in_location && <LocationStamp location={s.clock_in_location} label="Clock-in location" testid={`admin-loc-in-${s.id}`} />}
                    {s.clock_out_location && <LocationStamp location={s.clock_out_location} label="Clock-out location" testid={`admin-loc-out-${s.id}`} />}
                    {!s.clock_in_location && !s.clock_out_location && <span className="text-xs text-muted-foreground">—</span>}
                  </div>
                </TableCell>
                <TableCell><StatusBadge status={s.status} /></TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" disabled={locked} onClick={() => openEdit(s)} data-testid={`edit-shift-${s.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {data.shifts.some((s) => s.edit_history?.length > 0) && (
        <Card className="p-5 rounded-xl border-border">
          <h2 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2"><History className="h-4 w-4" /> Correction history</h2>
          <div className="space-y-2 text-sm">
            {data.shifts.flatMap((s) => (s.edit_history || []).map((h, i) => (
              <div key={s.id + i} className="border-b border-border pb-2">
                <span className="font-medium">{s.date_display}</span> — {h.field}: {h.original_time} → {h.corrected_time}
                <span className="text-muted-foreground"> • {h.reason} • by {h.by} on {h.at_display}</span>
              </div>
            )))}
          </div>
        </Card>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editShift} onOpenChange={(o) => !o && setEditShift(null)}>
        <DialogContent data-testid="edit-shift-dialog" className="sm:max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader><DialogTitle>Edit entry — {editShift?.date_display}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Clock-in time</Label>
              <Input type="datetime-local" value={editForm.clock_in} onChange={(e) => setEditForm({ ...editForm, clock_in: e.target.value })} data-testid="edit-clockin" /></div>
            <div className="space-y-2"><Label>Clock-out time</Label>
              <Input type="datetime-local" value={editForm.clock_out} onChange={(e) => setEditForm({ ...editForm, clock_out: e.target.value })} data-testid="edit-clockout" /></div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Breaks</Label>
                <Button type="button" size="sm" variant="ghost"
                  onClick={() => setEditForm({ ...editForm, breaks: [...(editForm.breaks || []), { start: "", end: "" }] })}
                  data-testid="edit-add-break">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add break
                </Button>
              </div>
              {(editForm.breaks || []).length === 0 && <p className="text-xs text-muted-foreground">No breaks recorded.</p>}
              {(editForm.breaks || []).map((b, i) => (
                <div key={i} className="rounded-lg border border-border p-3 space-y-2" data-testid={`edit-break-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Break {i + 1}</span>
                    <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-[hsl(8,54%,42%)]"
                      onClick={() => setEditForm({ ...editForm, breaks: editForm.breaks.filter((_, j) => j !== i) })}
                      data-testid={`edit-break-remove-${i}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                    </Button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <div className="space-y-1 min-w-0">
                      <Label className="text-xs">Start</Label>
                      <Input type="datetime-local" className="w-full" value={b.start}
                        onChange={(e) => {
                          const next = [...editForm.breaks];
                          next[i] = { ...next[i], start: e.target.value };
                          setEditForm({ ...editForm, breaks: next });
                        }} data-testid={`edit-break-start-${i}`} />
                    </div>
                    <div className="space-y-1 min-w-0">
                      <Label className="text-xs">End</Label>
                      <Input type="datetime-local" className="w-full" value={b.end}
                        onChange={(e) => {
                          const next = [...editForm.breaks];
                          next[i] = { ...next[i], end: e.target.value };
                          setEditForm({ ...editForm, breaks: next });
                        }} data-testid={`edit-break-end-${i}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-2"><Label>Reason for change (required)</Label>
              <Textarea value={editForm.reason} onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })} data-testid="edit-reason" /></div>
            <div className="space-y-2"><Label>Note (optional)</Label>
              <Textarea value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} placeholder="Context for this shift, e.g. covered reception" data-testid="edit-note" /></div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} disabled={!editForm.reason} data-testid="edit-save">Save Change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add entry dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-entry-dialog">
          <DialogHeader><DialogTitle>Add Missing Entry</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Clock-in time</Label>
              <Input type="datetime-local" value={addForm.clock_in} onChange={(e) => setAddForm({ ...addForm, clock_in: e.target.value })} data-testid="add-clockin" /></div>
            <div className="space-y-2"><Label>Clock-out time (optional)</Label>
              <Input type="datetime-local" value={addForm.clock_out} onChange={(e) => setAddForm({ ...addForm, clock_out: e.target.value })} data-testid="add-clockout" /></div>
            <div className="space-y-2"><Label>Reason (required)</Label>
              <Textarea value={addForm.reason} onChange={(e) => setAddForm({ ...addForm, reason: e.target.value })} data-testid="add-reason" /></div>
            <div className="space-y-2"><Label>Note (optional)</Label>
              <Textarea value={addForm.note} onChange={(e) => setAddForm({ ...addForm, note: e.target.value })} placeholder="Context for this shift" data-testid="add-note" /></div>
          </div>
          <DialogFooter>
            <Button onClick={addEntry} disabled={!addForm.clock_in || !addForm.reason} data-testid="add-entry-submit">Add Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
